import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { VFC } from "react";
import styled from "styled-components";
import { Markdown, SupTitle, Image } from "components";
import { PipelineRoutingLinkButton } from "components/Molecules/PipelineRoutingLinkButton";
import { FullArrow } from "icons";
import { Hero7Content } from "types/graphql/generated";
import { Graphic } from "./icons";

interface Props {
  className?: string;
  content: Hero7Content;
}

const Hero7: VFC<Props> = withErrorBoundary(
  ({
    content: {
      hero7SupTitle,
      hero7Title,
      hero7Description,
      hero7ButtonLabel,
      hero7ButtonLink,
      hero7Background: {
        data: { hero7Background },
      },
    },
  }) => {
    return (
      <StyledHero7 className="Content">
        <div className="Hero7__textContent">
          <SupTitle color="Color_secondary">{hero7SupTitle}</SupTitle>
          <h2 className="Hero7__title Font_80_88_600 xl:Font_64_72_600 md:Font_48_54_600">{hero7Title}</h2>
          <Markdown className="Hero7__description Color_secondary Font_24_32_450">{hero7Description}</Markdown>
          <PipelineRoutingLinkButton href={hero7ButtonLink} rightIcon={<FullArrow />} className="Hero7__button">
            {hero7ButtonLabel}
          </PipelineRoutingLinkButton>
        </div>
        <div className="Hero7__backgroundContainer">
          <Image
            src={hero7Background.src}
            alt={hero7Background.alt}
            fill
            style={{
              objectFit: "cover",
            }}
            className="Hero7__background"
            sizes="530px"
          />
          <Graphic className="Hero7__graphic" />
        </div>
      </StyledHero7>
    );
  },
  {}
);

const StyledHero7 = styled.section`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 40px;

  .Hero7__textContent {
    flex: 1;
  }

  .Hero7__title {
    margin-top: 32px;
  }

  .Hero7__description {
    max-width: 466px;
    margin-top: 24px;
  }

  .Hero7__button {
    width: fit-content;
    margin-top: 56px;
  }

  .Hero7__backgroundContainer {
    flex: 1;
    max-width: 530px;
    position: relative;
    min-height: 600px;
    height: 100%;
  }

  .Hero7__background {
    object-fit: cover;
  }

  .Hero7__graphic {
    position: absolute;
    width: 200px;
    height: 64px;
    right: 0;
    bottom: 0;
    z-index: 1;
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.lg}px`}) {
    .Hero7__backgroundContainer {
      display: none;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.md}px`}) {
    .Hero7__textContent {
      max-width: 100%;
    }

    .Hero7__title {
      margin-top: 16px;
    }

    .Hero7__description {
      margin-top: 16px;
    }

    .Hero7__button {
      margin-top: 40px;
    }
  }
`;

gql`
  fragment Hero7Content on ComponentHeroHero7 {
    hero7SupTitle
    hero7Title
    hero7Description
    hero7ButtonLabel
    hero7ButtonLink
    hero7Background {
      data {
        hero7Background: attributes {
          src: hash
          alt: alternativeText
          width
          height
        }
      }
    }
  }
`;

export { Hero7 };
export type { Props as Hero7Props };
